import chroma from 'chroma-js';
import { ComponentProps } from 'react';

interface Props extends Omit<ComponentProps<'svg'>, 'width' | 'height'> {
  polygon: {
    polygon: GeoJSON.Geometry;
    color?: string;
    stroke?: string;
  };
  width?: number;
  height?: number;
  padding?: number;
}

export const PolygonSvg = (props: Props) => {
  const { polygon, color, width = 100, height = 100, padding = 10 } = props;

  const polygonFill = color || '#E3C5F3';
  const polygonStroke = chroma(polygonFill).darken(1).hex();

  const geometry = polygon.polygon;

  if (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') {
    return <svg width={width} height={height} {...props}></svg>;
  }

  const allCoords: number[][] = [];
  const rings: number[][][] = [];

  if (geometry.type === 'Polygon') {
    rings.push(...geometry.coordinates);
    geometry.coordinates.forEach((ring) => {
      allCoords.push(...ring);
    });
  } else {
    geometry.coordinates.forEach((polygonCoords) => {
      rings.push(...polygonCoords);
      polygonCoords.forEach((ring) => {
        allCoords.push(...ring);
      });
    });
  }

  if (allCoords.length === 0) {
    return <svg width={width} height={height} {...props}></svg>;
  }

  const lons = allCoords.map((c) => c[0]);
  const lats = allCoords.map((c) => c[1]);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);

  const scaleX = (width - 2 * padding) / (maxLon - minLon);
  const scaleY = (height - 2 * padding) / (maxLat - minLat);
  const scale = Math.min(scaleX, scaleY);

  const ringToPoints = (ring: number[][]) => {
    return ring
      .map(([lon, lat]) => {
        const x = padding + (lon - minLon) * scale;
        const y = height - (padding + (lat - minLat) * scale);
        return `${x},${y}`;
      })
      .join(' ');
  };

  return (
    <svg width={width} height={height} {...props}>
      {rings.map((ring, idx) => (
        <polygon
          key={idx}
          points={ringToPoints(ring)}
          fill={polygonFill}
          stroke={polygonStroke}
          strokeWidth="2"
        />
      ))}
    </svg>
  );
};
